import { Container, Graphics, LINE_JOIN, Text, TextMetrics, TextStyle } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import { ChatInputState, MAX_CHAT_LENGTH } from "./chat_input_model"
import {
    CARET_COLOR,
    CHAT_CORNER,
    CHAT_PAD_X,
    CHAT_WIDTH,
    COMMAND_COLOR,
    DANGER_COLOR,
    INPUT_FONT_SIZE,
    INPUT_HEIGHT,
    INPUT_COUNTER_FONT_SIZE,
    INPUT_PANEL_ALPHA,
    PANEL_FILL,
    RIM_ACTIVE,
    SHADOW_ALPHA,
    SHADOW_COLOR,
    SHADOW_OFFSET,
    TEXT_COLOR,
    WARN_COLOR,
} from "./chat_theme"

const BLINK_MS = 530
// Start warning about the length cap once the line is this close to it. Earlier than that it is
// noise; later and the player hits the cap mid-word with no warning at all.
const COUNTER_FROM = MAX_CHAT_LENGTH - 30
const CARET_W = 1.5
const CHEVRON_GAP = 5
// Air kept to the right of the text so a caret at the end of a full line is never flush against
// the rim.
const TAIL_PAD = 3

// The chat input line: a real field rather than bare text over the grass.
//
// It draws from a ChatInputState, so it can show a caret ANYWHERE in the line (not only at the
// end), and it scrolls horizontally inside its own mask to keep that caret in view -- a 150
// character message is far longer than the field is wide.
//
// It says nothing about the slash commands the game understands: those are not advertised, and a
// line starting with "/" is only tinted, so the player can see they are typing one.
export class Typing extends Container {
    private shadow: Graphics
    private panel: Graphics
    private chevron: Text
    private textNode: Text
    private counter: Text
    private caret: Graphics
    private viewport: Container
    private textMask: Graphics
    private blinkInterval: any
    private textX: number = 0
    private viewportWidth: number = 0

    constructor() {
        super()

        this.shadow = new Graphics()
        this.panel = new Graphics()
        this.addChild(this.shadow)
        this.addChild(this.panel)

        this.chevron = new TextRenderer()
            .fontSize(INPUT_FONT_SIZE)
            .color(RIM_ACTIVE)
            .bold()
            .strokeThickness(0)
            .render(">") as Text
        this.chevron.x = CHAT_PAD_X
        this.addChild(this.chevron)

        this.textX = CHAT_PAD_X + (this.chevron.width || INPUT_FONT_SIZE * 0.6) + CHEVRON_GAP

        // Everything that scrolls lives in the viewport; the mask is what makes a long line stop
        // at the rim instead of running on across the world behind the panel.
        this.viewport = new Container()
        this.viewport.x = this.textX
        this.textMask = new Graphics()
        this.viewport.mask = this.textMask
        this.addChild(this.textMask)
        this.addChild(this.viewport)

        this.textNode = new TextRenderer()
            .fontSize(INPUT_FONT_SIZE)
            .color(TEXT_COLOR)
            .strokeThickness(0)
            .render("") as Text
        this.caret = new Graphics()
        this.viewport.addChild(this.textNode)
        this.viewport.addChild(this.caret)

        this.counter = new TextRenderer()
            .fontSize(INPUT_COUNTER_FONT_SIZE)
            .color(WARN_COLOR)
            .strokeThickness(0)
            .render("") as Text
        this.counter.visible = false
        this.addChild(this.counter)

        this.drawPanel()
        this.layoutRows()
        this.visible = false
    }

    update = (state: ChatInputState) => {
        this.visible = true
        this.startBlink()

        const isCommand = state.value.startsWith("/")
        this.textNode.text = state.value
        this.textNode.tint = isCommand ? COMMAND_COLOR : TEXT_COLOR

        const remaining = MAX_CHAT_LENGTH - state.value.length
        const showCounter = state.value.length >= COUNTER_FROM
        this.counter.visible = showCounter
        if (showCounter) {
            this.counter.text = `${remaining}`
            this.counter.tint = remaining <= 10 ? DANGER_COLOR : WARN_COLOR
        }

        this.layoutRows()

        const caretX = this.measure(state.value.slice(0, state.caret))
        this.caret.x = caretX

        // Keep the caret inside the field: scroll only as far as it has to, and never past the
        // start, so a short line always sits against the chevron.
        const maxScroll = Math.max(0, this.measure(state.value) - this.viewportWidth + TAIL_PAD)
        let scroll = -this.textNode.x
        if (caretX - scroll > this.viewportWidth - TAIL_PAD) {
            scroll = caretX - this.viewportWidth + TAIL_PAD
        }
        if (caretX - scroll < 0) {
            scroll = caretX
        }
        scroll = Math.max(0, Math.min(scroll, maxScroll))
        this.textNode.x = -scroll
        this.caret.x -= scroll
    }

    removeText = () => {
        this.visible = false
        this.textNode.text = ""
        this.textNode.x = 0
        this.stopBlink()
    }

    // Width of a prefix of the line, in the field's own font. TextMetrics caches per string+style,
    // so re-measuring on every keystroke is cheap.
    private measure = (text: string): number =>
        text ? TextMetrics.measureText(text, this.textNode.style as TextStyle).width : 0

    private layoutRows = () => {
        const lineH = this.textNode.height || INPUT_FONT_SIZE + 4
        const baseline = Math.round((INPUT_HEIGHT - lineH) / 2)
        this.chevron.y = Math.round((INPUT_HEIGHT - (this.chevron.height || lineH)) / 2)
        this.viewport.y = baseline

        // The countdown to the length cap is pinned to the right edge, and takes its width out of
        // the field the text may scroll in.
        const tail = this.counter.visible ? this.counter : undefined
        const tailWidth = tail ? (tail.width || 0) + CHEVRON_GAP : 0
        if (tail) {
            tail.x = CHAT_WIDTH - CHAT_PAD_X - (tail.width || 0)
            tail.y = Math.round((INPUT_HEIGHT - (tail.height || INPUT_COUNTER_FONT_SIZE)) / 2) + 1
        }

        this.viewportWidth = Math.max(40, CHAT_WIDTH - this.textX - CHAT_PAD_X - tailWidth)

        this.textMask.clear()
        this.textMask.beginFill(0xffffff)
        this.textMask.drawRect(this.textX, 0, this.viewportWidth, INPUT_HEIGHT)
        this.textMask.endFill()

        this.caret.clear()
        this.caret.beginFill(CARET_COLOR, 0.95)
        this.caret.drawRect(0, 1, CARET_W, Math.max(10, lineH - 2))
        this.caret.endFill()
    }

    private drawPanel = () => {
        this.shadow.clear()
        this.shadow.beginFill(SHADOW_COLOR, SHADOW_ALPHA)
        this.shadow.drawRoundedRect(SHADOW_OFFSET, SHADOW_OFFSET, CHAT_WIDTH, INPUT_HEIGHT, CHAT_CORNER)
        this.shadow.endFill()

        this.panel.clear()
        this.panel.lineStyle({ width: 1, color: RIM_ACTIVE, alpha: 0.55, join: LINE_JOIN.ROUND })
        this.panel.beginFill(PANEL_FILL, INPUT_PANEL_ALPHA)
        this.panel.drawRoundedRect(0, 0, CHAT_WIDTH, INPUT_HEIGHT, CHAT_CORNER)
        this.panel.endFill()
    }

    // The caret is solid while the player is typing and only resumes blinking once they stop --
    // a caret that blinks THROUGH the keystroke reads as dropped input.
    private startBlink = () => {
        this.caret.visible = true
        if (this.blinkInterval) {
            clearInterval(this.blinkInterval)
        }
        this.blinkInterval = setInterval(() => {
            this.caret.visible = !this.caret.visible
        }, BLINK_MS)
    }

    private stopBlink = () => {
        clearInterval(this.blinkInterval)
        this.blinkInterval = undefined
        this.caret.visible = false
    }
}
